import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useApprovalPolicies(workspaceId: string) {
  return useQuery({
    queryKey: ['approval-policies', workspaceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('approval_policies')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!workspaceId
  });
}

export function useCreateApprovalPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (policy: any) => {
      const { data, error } = await supabase
        .from('approval_policies')
        .insert(policy)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approval-policies'] });
      toast.success('Approval policy created');
    },
    onError: (error: Error) => {
      toast.error('Failed to create policy: ' + error.message);
    }
  });
}

export function useInitiateApprovalWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      billId: string;
      workspaceId: string;
      billAmount: number;
      vendorId?: string;
      commodityType?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('approval-workflow-manager', {
        body: input
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bill-approvals'] });
      if (data.autoApproved) {
        toast.success('Bill auto-approved');
      } else {
        toast.info('Approval workflow initiated');
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to initiate approval: ' + error.message);
    }
  });
}

export function useBillApprovals(billId?: string, userId?: string) {
  return useQuery({
    queryKey: ['bill-approvals', billId, userId],
    queryFn: async () => {
      let query = supabase
        .from('bill_approvals')
        .select('*');

      if (billId) {
        query = query.eq('bill_id', billId);
      }

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    }
  });
}

export function useApprovalDelegates(userId: string) {
  return useQuery({
    queryKey: ['approval-delegates', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('approval_delegates')
        .select('*')
        .or(`delegator_id.eq.${userId},delegate_id.eq.${userId}`)
        .eq('is_active', true)
        .gte('valid_until', new Date().toISOString());

      if (error) throw error;
      return data;
    },
    enabled: !!userId
  });
}

export function useCreateDelegate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (delegate: any) => {
      const { data, error } = await supabase
        .from('approval_delegates')
        .insert(delegate)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['approval-delegates'] });
      toast.success('Delegate added');
    },
    onError: (error: Error) => {
      toast.error('Failed to add delegate: ' + error.message);
    }
  });
}
